import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LeftOutline, DeleteOutline } from 'antd-mobile-icons';
import { Toast, Dialog } from 'antd-mobile';

interface StorageItem {
  key: string;
  value: string;
  type: 'localStorage' | 'sessionStorage';
}

export default function DebugStorage() {
  const navigate = useNavigate();
  const [storageItems, setStorageItems] = useState<StorageItem[]>([]);
  const [vConsoleEnabled, setVConsoleEnabled] = useState(false);

  // 加载所有存储数据
  const loadStorageData = () => {
    const items: StorageItem[] = [];

    // 获取 localStorage 中的所有项
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || '';
        items.push({ key, value, type: 'localStorage' });
      }
    }

    // 获取 sessionStorage 中的所有项
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key) {
        const value = sessionStorage.getItem(key) || '';
        items.push({ key, value, type: 'sessionStorage' });
      }
    }

    setStorageItems(items);
  };

  useEffect(() => {
    loadStorageData();
    // 检查VConsole是否已经启动
    if (typeof (window as any).vConsole !== 'undefined') {
      setVConsoleEnabled(true);
    }
  }, []);

  // 打开VConsole
  const handleOpenVConsole = () => {
    try {
      if (typeof (window as any).VConsole !== 'undefined') {
        if (!vConsoleEnabled) {
          (window as any).vConsole = new (window as any).VConsole();
          setVConsoleEnabled(true);
          console.log('✅ VConsole已启动');
          Toast.show({
            content: 'VConsole已启动',
            icon: 'success'
          });
        } else {
          console.log('ℹ️ VConsole已经在运行中');
          Toast.show({
            content: 'VConsole已经在运行',
            icon: 'success'
          });
        }
      } else {
        console.error('❌ VConsole未找到，请确保CDN已正确加载');
        Toast.show({
          content: 'VConsole未找到',
          icon: 'fail'
        });
      }
    } catch (error) {
      console.error('❌ 启动VConsole失败:', error);
      Toast.show({
        content: '启动VConsole失败',
        icon: 'fail'
      });
    }
  };

  // 复制存储项的值
  const handleCopyValue = async (item: StorageItem) => {
    try {
      await navigator.clipboard.writeText(item.value);
      console.log(`📋 已复制 ${item.type} 中的 "${item.key}" 的值`);
      Toast.show({
        content: '已复制到剪贴板',
        icon: 'success'
      });
    } catch (error) {
      console.error('❌ 复制失败:', error);
      // 降级方案：使用传统方法
      try {
        const textArea = document.createElement('textarea');
        textArea.value = item.value;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        console.log(`📋 已复制 ${item.type} 中的 "${item.key}" 的值（降级方案）`);
        Toast.show({
          content: '已复制到剪贴板',
          icon: 'success'
        });
      } catch (e) {
        Toast.show({
          content: '复制失败',
          icon: 'fail'
        });
      }
    }
  };

  // 编辑存储项的值
  const handleEditValue = (item: StorageItem) => {
    Dialog.confirm({
      title: `编辑 ${item.key}`,
      content: (
        <div className="mt-2">
          <textarea
            id="edit-storage-value"
            className="w-full p-2 border border-gray-300 rounded min-h-32 text-sm font-mono"
            defaultValue={item.value}
          />
        </div>
      ),
      confirmText: '保存',
      cancelText: '取消',
      onConfirm: () => {
        const textarea = document.getElementById('edit-storage-value') as HTMLTextAreaElement;
        const newValue = textarea?.value || '';
        
        try {
          if (item.type === 'localStorage') {
            localStorage.setItem(item.key, newValue);
            console.log(`✏️ 已更新 localStorage 中的 "${item.key}"`);
          } else {
            sessionStorage.setItem(item.key, newValue);
            console.log(`✏️ 已更新 sessionStorage 中的 "${item.key}"`);
          }
          Toast.show({
            content: '保存成功',
            icon: 'success'
          });
          loadStorageData();
        } catch (error) {
          console.error('❌ 保存失败:', error);
          Toast.show({
            content: '保存失败',
            icon: 'fail'
          });
        }
      }
    });
  };

  // 清除单个存储项
  const handleClearItem = (item: StorageItem) => {
    Dialog.confirm({
      title: '确认删除',
      content: `确定要删除 "${item.key}" 吗？`,
      confirmText: '删除',
      cancelText: '取消',
      onConfirm: () => {
        try {
          if (item.type === 'localStorage') {
            localStorage.removeItem(item.key);
            console.log(`🗑️ 已清除 localStorage 中的 "${item.key}"`);
            Toast.show({
              content: `已清除 localStorage: ${item.key}`,
              icon: 'success'
            });
          } else {
            sessionStorage.removeItem(item.key);
            console.log(`🗑️ 已清除 sessionStorage 中的 "${item.key}"`);
            Toast.show({
              content: `已清除 sessionStorage: ${item.key}`,
              icon: 'success'
            });
          }
          loadStorageData();
        } catch (error) {
          console.error('❌ 清除存储项失败:', error);
          Toast.show({
            content: '清除失败',
            icon: 'fail'
          });
        }
      }
    });
  };

  // 清除所有 localStorage
  const handleClearAllLocalStorage = () => {
    Dialog.confirm({
      title: '确认清除',
      content: '确定要清除所有 localStorage 吗？此操作不可恢复！',
      confirmText: '清除',
      cancelText: '取消',
      onConfirm: () => {
        try {
          localStorage.clear();
          console.log('🧹 已清除所有 localStorage');
          Toast.show({
            content: '已清除所有 localStorage',
            icon: 'success'
          });
          loadStorageData();
        } catch (error) {
          console.error('❌ 清除所有 localStorage 失败:', error);
          Toast.show({
            content: '清除失败',
            icon: 'fail'
          });
        }
      }
    });
  };

  // 清除所有 sessionStorage
  const handleClearAllSessionStorage = () => {
    Dialog.confirm({
      title: '确认清除',
      content: '确定要清除所有 sessionStorage 吗？此操作不可恢复！',
      confirmText: '清除',
      cancelText: '取消',
      onConfirm: () => {
        try {
          sessionStorage.clear();
          console.log('🧹 已清除所有 sessionStorage');
          Toast.show({
            content: '已清除所有 sessionStorage',
            icon: 'success'
          });
          loadStorageData();
        } catch (error) {
          console.error('❌ 清除所有 sessionStorage 失败:', error);
          Toast.show({
            content: '清除失败',
            icon: 'fail'
          });
        }
      }
    });
  };

  // 清除所有存储
  const handleClearAll = () => {
    Dialog.confirm({
      title: '⚠️ 危险操作',
      content: '确定要清除所有存储吗？包括 localStorage 和 sessionStorage，此操作不可恢复！',
      confirmText: '全部清除',
      cancelText: '取消',
      onConfirm: () => {
        try {
          localStorage.clear();
          sessionStorage.clear();
          console.log('🧹🧹 已清除所有存储（localStorage + sessionStorage）');
          Toast.show({
            content: '已清除所有存储',
            icon: 'success'
          });
          loadStorageData();
        } catch (error) {
          console.error('❌ 清除所有存储失败:', error);
          Toast.show({
            content: '清除失败',
            icon: 'fail'
          });
        }
      }
    });
  };

  // 格式化显示值（截断过长的内容）
  const formatValue = (value: string, maxLength: number = 50) => {
    if (value.length <= maxLength) {
      return value;
    }
    return value.substring(0, maxLength) + '...';
  };

  const localStorageItems = storageItems.filter(item => item.type === 'localStorage');
  const sessionStorageItems = storageItems.filter(item => item.type === 'sessionStorage');

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* 头部导航 */}
      <div className="sticky top-0 z-10 bg-white shadow-sm">
        <div className="flex items-center h-14 px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center w-8 h-8 -ml-2"
          >
            <LeftOutline fontSize={20} className="text-gray-700" />
          </button>
          <h1 className="flex-1 text-center text-lg font-medium text-gray-900 -ml-8">
            🔧 存储调试
          </h1>
        </div>
      </div>

      <div className="p-4 max-w-4xl mx-auto">
        {/* VConsole 控制 */}
        <motion.div 
          className="bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg p-4 mb-4 shadow-lg"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center justify-between">
            <div className="text-white">
              <div className="font-medium text-base mb-1">🛠️ VConsole 调试工具</div>
              <div className="text-sm text-white/80">
                {vConsoleEnabled ? '已启动' : '点击启动移动端调试控制台'}
              </div>
            </div>
            <button
              onClick={handleOpenVConsole}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                vConsoleEnabled 
                  ? 'bg-white/20 text-white cursor-default' 
                  : 'bg-white text-purple-600 hover:bg-white/90'
              }`}
            >
              {vConsoleEnabled ? '✅ 已启动' : '启动'}
            </button>
          </div>
        </motion.div>

        {/* 统计信息 */}
        <motion.div 
          className="bg-white rounded-lg p-4 mb-4 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-medium">📊 存储统计</h2>
            <button
              onClick={handleClearAll}
              className="px-4 py-1.5 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors"
            >
              清除全部
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">{localStorageItems.length}</div>
              <div className="text-sm text-gray-600 mt-1">localStorage</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">{sessionStorageItems.length}</div>
              <div className="text-sm text-gray-600 mt-1">sessionStorage</div>
            </div>
          </div>
        </motion.div>

        {/* localStorage 列表 */}
        <motion.div 
          className="bg-white rounded-lg p-4 mb-4 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-medium flex items-center">
              <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
              localStorage ({localStorageItems.length})
            </h2>
            {localStorageItems.length > 0 && (
              <button
                onClick={handleClearAllLocalStorage}
                className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
              >
                清除全部
              </button>
            )}
          </div>
          
          {localStorageItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              暂无 localStorage 数据
            </div>
          ) : (
            <div className="space-y-2">
              {localStorageItems.map((item, index) => (
                <motion.div
                  key={`local-${item.key}-${index}`}
                  className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-2">
                      <div className="font-medium text-sm text-gray-900 mb-1 break-all">
                        🔑 {item.key}
                      </div>
                      <div className="text-xs text-gray-500 break-all font-mono bg-gray-50 p-2 rounded">
                        {formatValue(item.value, 100)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleCopyValue(item)}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors text-xs"
                        title="复制"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => handleEditValue(item)}
                        className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors text-xs"
                        title="编辑"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleClearItem(item)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="删除"
                      >
                        <DeleteOutline fontSize={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* sessionStorage 列表 */}
        <motion.div 
          className="bg-white rounded-lg p-4 mb-4 shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-base font-medium flex items-center">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
              sessionStorage ({sessionStorageItems.length})
            </h2>
            {sessionStorageItems.length > 0 && (
              <button
                onClick={handleClearAllSessionStorage}
                className="px-3 py-1 bg-green-500 text-white text-sm rounded hover:bg-green-600 transition-colors"
              >
                清除全部
              </button>
            )}
          </div>
          
          {sessionStorageItems.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              暂无 sessionStorage 数据
            </div>
          ) : (
            <div className="space-y-2">
              {sessionStorageItems.map((item, index) => (
                <motion.div
                  key={`session-${item.key}-${index}`}
                  className="border border-gray-200 rounded-lg p-3 hover:border-green-300 transition-colors"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0 mr-2">
                      <div className="font-medium text-sm text-gray-900 mb-1 break-all">
                        🔑 {item.key}
                      </div>
                      <div className="text-xs text-gray-500 break-all font-mono bg-gray-50 p-2 rounded">
                        {formatValue(item.value, 100)}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleCopyValue(item)}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors text-xs"
                        title="复制"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => handleEditValue(item)}
                        className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors text-xs"
                        title="编辑"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleClearItem(item)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="删除"
                      >
                        <DeleteOutline fontSize={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* 说明 */}
        <motion.div 
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-gray-600"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="font-medium text-yellow-800 mb-2">⚠️ 注意事项</div>
          <ul className="list-disc list-inside space-y-1">
            <li>清除存储可能导致需要重新登录</li>
            <li>编辑后的数据会立即生效</li>
            <li>删除操作需要确认，请谨慎操作</li>
            <li>此页面仅用于调试目的</li>
          </ul>
        </motion.div>
      </div>
    </div>
  );
}
